import { cva } from 'class-variance-authority';

import Icon from 'molecules/Icon';

import { analyticsClient } from 'utils/analytics/client';

import type { IconIds } from 'molecules/Icon/types';
import type { FC, MouseEvent, MouseEventHandler } from 'react';

interface SocialButtonProps {
  icon: IconIds;
  copy?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  href?: string;
}

const linkProps = {
  target: '_blank',
  rel: 'noopener noreferrer',
} as const;

const buttonStyles = cva(
  'flex items-center justify-center gap-2 rounded-lg border border-primary-900 bg-primary-25 p-[9px] text-primary-900 transition-colors focus-outline-primary hover:bg-primary-100 dark:border-gray-400 dark:bg-gray-100/30 dark:text-gray-300 dark:hover:bg-gray-100/50',
  { variants: { hasCopy: { true: 'px-[15px]' } } },
);

const SocialButton: FC<SocialButtonProps> = ({ icon, copy, onClick, href }) => {
  const Button = href ? 'a' : 'button';

  const clickHandler = (e: MouseEvent<HTMLButtonElement>) => {
    analyticsClient.track('Clicked CTA', {
      name: 'Social Icon',
      external: href,
      segmentType: 'App Page',
    });

    onClick?.(e);
  };

  return (
    <Button
      className={buttonStyles({ hasCopy: !!copy })}
      onClick={e => clickHandler(e as MouseEvent<HTMLButtonElement>)}
      href={href}
      aria-label="Social Icon"
      {...(href ? linkProps : {})}
    >
      <Icon icon={icon} size={20} className="stroke-2" />
      {copy && <span className="text-sm font-semibold leading-none">{copy}</span>}
    </Button>
  );
};

export default SocialButton;
